import type { CalendarRecurrenceFreq, CalendarRecurrenceRule } from '../../types/calendarWorkspace';

export type RecurrenceFormState = {
  freq: CalendarRecurrenceFreq;
  interval: number;
  nth: number;
  /** never = no ending date; on = ends on `until` */
  endMode: 'never' | 'on';
  until: string;
};

export function emptyRecurrenceForm(): RecurrenceFormState {
  return {
    freq: 'none',
    interval: 1,
    nth: 3,
    endMode: 'never',
    until: '',
  };
}

export function recurrenceFormFromRule(
  rule: CalendarRecurrenceRule | null | undefined,
): RecurrenceFormState {
  if (!rule || rule.freq === 'none') return emptyRecurrenceForm();
  const until = (rule.until ?? '').trim();
  return {
    freq: rule.freq,
    interval: Math.max(1, rule.interval ?? 1),
    nth: Math.max(1, rule.nth ?? 3),
    endMode: until ? 'on' : 'never',
    until,
  };
}

export function ruleFromRecurrenceForm(
  form: RecurrenceFormState,
): CalendarRecurrenceRule | null {
  if (form.freq === 'none') return null;
  return {
    freq: form.freq,
    interval: form.interval,
    nth: form.nth,
    until: form.endMode === 'on' && form.until.trim() ? form.until.trim() : null,
  };
}

type Props = {
  value: RecurrenceFormState;
  onChange: (next: RecurrenceFormState) => void;
  disabled?: boolean;
};

export function CalendarRecurrenceFields({ value, onChange, disabled }: Props) {
  const set = (patch: Partial<RecurrenceFormState>) => onChange({ ...value, ...patch });

  return (
    <div className="cal-recur">
      <label className="cal-field">
        <span>Repeats</span>
        <select
          value={value.freq}
          disabled={disabled}
          onChange={(e) =>
            set({
              freq: e.target.value as CalendarRecurrenceFreq,
              endMode: e.target.value === 'none' ? 'never' : value.endMode,
            })
          }
        >
          <option value="none">Does not repeat</option>
          <option value="daily">Every N days</option>
          <option value="weekly">Every week</option>
          <option value="monthly">Every month</option>
          <option value="nth_workday">Nth working day of month</option>
        </select>
      </label>

      {value.freq === 'daily' || value.freq === 'weekly' || value.freq === 'monthly' ? (
        <label className="cal-field">
          <span>
            {value.freq === 'daily'
              ? 'Every how many days'
              : value.freq === 'weekly'
                ? 'Every how many weeks'
                : 'Every how many months'}
          </span>
          <input
            type="number"
            min={1}
            max={365}
            disabled={disabled}
            value={value.interval}
            onChange={(e) => set({ interval: Math.max(1, Number(e.target.value) || 1) })}
          />
        </label>
      ) : null}

      {value.freq === 'nth_workday' ? (
        <label className="cal-field">
          <span>Which working day (Mon–Fri)</span>
          <input
            type="number"
            min={1}
            max={23}
            disabled={disabled}
            value={value.nth}
            onChange={(e) =>
              set({ nth: Math.max(1, Math.min(23, Number(e.target.value) || 1)) })
            }
          />
          <span className="cal-field__hint">e.g. 3 = 3rd working day of each month</span>
        </label>
      ) : null}

      {value.freq !== 'none' ? (
        <>
          <label className="cal-field">
            <span>Ending</span>
            <select
              value={value.endMode}
              disabled={disabled}
              onChange={(e) =>
                set({
                  endMode: e.target.value as 'never' | 'on',
                  until: e.target.value === 'never' ? '' : value.until,
                })
              }
            >
              <option value="never">No ending date</option>
              <option value="on">Ends on a date</option>
            </select>
          </label>
          {value.endMode === 'on' ? (
            <label className="cal-field">
              <span>Ending date</span>
              <input
                type="date"
                disabled={disabled}
                value={value.until}
                onChange={(e) => set({ until: e.target.value, endMode: 'on' })}
              />
            </label>
          ) : (
            <p className="cal-field__hint cal-recur__hint">Series continues with no end date.</p>
          )}
        </>
      ) : null}
    </div>
  );
}
